import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  IconButton,
  Stack,
  ImageList,
  ImageListItem,
  Modal,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as TodoIcon,
  Schedule as InProgressIcon,
  ContentCopy as CloneIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface TaskDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  task: any;
  isPortal?: boolean;
  onClone?: (task: any) => void;
}

export const TaskDetailsDialog: React.FC<TaskDetailsDialogProps> = ({ open, onClose, task, isPortal = false, onClone }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'done':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_progress':
      case 'in progress':
        return <InProgressIcon fontSize="small" />;
      default:
        return <TodoIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'done':
        return 'success';
      case 'in_progress':
      case 'in progress':
        return 'primary';
      case 'todo':
      case 'pending':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Not set';
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatDateTime = (date: string | Date | null) => {
    if (!date) return 'Not set';
    try {
      return format(new Date(date), 'MMM dd, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isPortal ? 'Service Request Details' : 'Task Details'}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!task ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Typography color="text.secondary">Loading task details...</Typography>
          </Box>
        ) : (
        <Stack spacing={3}>
          {/* Title and Status */}
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
              {task.title || 'Untitled Task'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {task.category && (
                <Chip
                  label={task.category.name || task.category.title || 'General'}
                  size="small"
                  sx={{
                    backgroundColor: '#6B7280',
                    color: '#fff',
                  }}
                />
              )}
              {task.priority && (
                <Chip
                  icon={<FlagIcon fontSize="small" />}
                  label={task.priority}
                  color={getPriorityColor(task.priority)}
                  size="small"
                />
              )}
              {task.type && (
                <Chip
                  label={task.type}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Divider />

          {/* Description */}
          {task.description && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DescriptionIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {task.description}
              </Typography>
            </Box>
          )}

          {/* Details Grid */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(task.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Scheduled Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(task.scheduledDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {task.assignedTo && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Assigned To
                    </Typography>
                    <Typography variant="body2">
                      {task.assignedTo.name || task.assignedTo.email || 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {task.completedAt && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" color="success" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(task.completedAt)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Additional Information */}
          {(task.customer || task.lead || task.opportunity) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Related To
                </Typography>
                <Stack spacing={1}>
                  {task.customer && (
                    <Typography variant="body2">
                      <strong>Customer:</strong> {task.customer.displayName || task.customer.company || 'Unknown'}
                    </Typography>
                  )}
                  {task.lead && (
                    <Typography variant="body2">
                      <strong>Lead:</strong> {task.lead.name || 'Unknown'}
                    </Typography>
                  )}
                  {task.opportunity && (
                    <Typography variant="body2">
                      <strong>Opportunity:</strong> {task.opportunity.name || 'Unknown'}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </>
          )}

          {/* Notes */}
          {task.notes && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {task.notes}
                </Typography>
              </Box>
            </>
          )}

          {/* Attachments */}
          {task.attachments && task.attachments.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Attachments ({task.attachments.length})
                </Typography>
                <ImageList sx={{ width: '100%', height: 120 }} cols={4} rowHeight={100}>
                  {task.attachments.map((attachment: any) => {
                    const imageUrl = attachment.fileUrl || attachment.file?.url || attachment.file?.fileUrl;
                    const fileName = attachment.fileName || attachment.file?.name || attachment.file?.fileName || 'Image';
                    
                    if (!imageUrl) return null;
                    
                    return (
                      <ImageListItem 
                        key={attachment.id}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => setSelectedImage(imageUrl)}
                      >
                        <img
                          src={imageUrl}
                          alt={fileName}
                          loading="lazy"
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              </Box>
            </>
          )}
        </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {onClone && !isPortal && (
          <Button
            startIcon={<CloneIcon />}
            onClick={() => onClone(task)}
            variant="outlined"
          >
            Clone Task
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>

      {/* Full-screen image modal */}
      <Modal
      open={!!selectedImage}
      onClose={() => setSelectedImage(null)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          outline: 'none',
        }}
        onClick={() => setSelectedImage(null)}
      >
        <IconButton
          onClick={() => setSelectedImage(null)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        {selectedImage && (
          <img
            src={selectedImage}
            alt="Full size"
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>
    </Modal>
    </>
  );
};